import React, { useState, useEffect } from 'react';
import { FiPlus } from 'react-icons/fi';
import '../styles/DashboardTimeCard.css';
import AdminTimeCard from './AdminTimeCard';
import ManagerTimeCard from './ManagerTimeCard';
import CleanerTimeCard from './CleanerTimeCard';
import ContractorTimeCard from './ContractorTimeCard';
import AddManualTime from './AddManualTime';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { toast } from 'react-toastify';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';

const ROLE_TABS = ['Admin', 'Manager', 'Cleaner', 'Contractor'];

function toISODate(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateFromISO(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const normalizeRole = (role) =>
  String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[/\s-]+/g, '_');

const hasAnyRole = (roles = [], expectedRoles = []) => {
  const normalizedExpected = expectedRoles.map(normalizeRole);
  return (roles || []).some((role) =>
    normalizedExpected.includes(normalizeRole(role)),
  );
};

function Admin({ members, selectedUserId, selectedDate, refreshKey }) {
  return <AdminTimeCard members={members} selectedUserId={selectedUserId} selectedDate={selectedDate} refreshKey={refreshKey} />;
}
function Manager({ members, selectedUserId, selectedDate, refreshKey }) {
  return <ManagerTimeCard members={members} selectedUserId={selectedUserId} selectedDate={selectedDate} refreshKey={refreshKey} />;
}
function Cleaner({ members, selectedUserId, selectedDate, refreshKey }) {
  return <CleanerTimeCard members={members} selectedUserId={selectedUserId} selectedDate={selectedDate} refreshKey={refreshKey} />;
}
function Contractor({ members, selectedUserId, selectedDate, refreshKey }) {
  return <ContractorTimeCard members={members} selectedUserId={selectedUserId} selectedDate={selectedDate} refreshKey={refreshKey} />;
}

export default function DashboardTimeCard() {
  const { get, patch } = useAdminPageSearch();
  const urlTab = get('tab');
  const activeTab = ROLE_TABS.includes(urlTab) ? urlTab : 'Admin';
  const showAddCard = get('action') === 'add';
  const urlMember = get('member');
  const urlDate = dateFromISO(get('date'));
  const [members, setMembers] = useState({ Admin: [], Manager: [], Cleaner: [], Contractor: [] });
  const [autoUserId, setAutoUserId] = useState(null);
  const selectedUserId = urlMember || autoUserId;
  const [selectedDate, setSelectedDate] = useState(() => urlDate || new Date());
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch team members from backend
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const query = `
          query GetAllUser {
            getAllUser {
              id
              firstName
              lastName
              email
              roles
            }
          }
        `;

        const data = await graphqlRequest(query);
        const allUsers = data?.getAllUser || [];

        // Filter users by role
        const adminUsers = allUsers.filter((user) =>
          hasAnyRole(user.roles, ['ADMIN', 'SUPER_ADMIN', 'BRANCH_SUPER_ADMIN'])
        );
        const managerUsers = allUsers.filter((user) =>
          hasAnyRole(user.roles, ['OPERATIONS_MANAGER'])
        );
        const cleanerUsers = allUsers.filter((user) =>
          hasAnyRole(user.roles, ['FIELD_CLEANER'])
        );
        const contractorUsers = allUsers.filter((user) =>
          hasAnyRole(user.roles, ['SCHEDULER_DISPATCHER', 'SUPERVISOR', 'CONTRACTOR'])
        );

        // Format members for dropdown with employee IDs matching Team Member page
        const formatMembers = (users, rolePrefix) => 
          users.map((user, index) => {
            // Generate employee ID same way as Team Member page: ADM001, MGM001, CLR001, CTR001
            const employeeId = `${rolePrefix}${String(index + 1).padStart(3, "0")}`;
            return {
              id: user.id,
              employeeId: employeeId,
              name: `${user.firstName} ${user.lastName}`,
              email: user.email,
            };
          });

        setMembers({
          Admin: formatMembers(adminUsers, 'ADM'),
          Manager: formatMembers(managerUsers, 'MGM'),
          Cleaner: formatMembers(cleanerUsers, 'CLR'),
          Contractor: formatMembers(contractorUsers, 'CTR'),
        });

        // Set first member as selected by default
        const currentTabMembers = formatMembers(
          activeTab === 'Admin' ? adminUsers :
          activeTab === 'Manager' ? managerUsers :
          activeTab === 'Cleaner' ? cleanerUsers : contractorUsers
        );
        if (currentTabMembers.length > 0 && !urlMember && !selectedUserId) {
          setAutoUserId(currentTabMembers[0].id);
        }
      } catch (err) {
        console.error('Error fetching members:', err);
        toast.error('Failed to fetch team members');
      }
    };

    fetchMembers();
  }, [activeTab, refreshKey, selectedUserId, urlMember]);

  // Update selected user when tab changes
  useEffect(() => {
    const currentTabMembers = members[activeTab] || [];
    if (currentTabMembers.length > 0) {
      const currentUser = currentTabMembers.find(m => m.id === selectedUserId);
      if (!currentUser) {
        const nextId = currentTabMembers[0].id;
        if (urlMember) patch({ member: nextId });
        else setAutoUserId(nextId);
      }
    }
  }, [activeTab, members, selectedUserId, urlMember, patch]);

  const handleMemberAdded = () => {
    patch({}, ['action']);
    setTimeout(() => {
      setRefreshKey((prev) => prev + 1);
    }, 300);
  };

  const handleDateChange = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (direction === 'next') {
      newDate.setDate(newDate.getDate() + 1);
    } else {
      newDate.setTime(new Date().getTime());
    }
    setSelectedDate(newDate);
    patch({ date: toISODate(newDate) });
  };

  const formatDateDisplay = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    const isToday = dateToCheck.getTime() === today.getTime();
    if (isToday) return 'Today';
    return dateToCheck.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderComponent = () => {
    if (showAddCard) {
      const selectedMember = members[activeTab]?.find(m => m.id === selectedUserId);
      return (
        <AddManualTime 
          onClose={() => patch({}, ['action'])}
          selectedMemberName={selectedMember?.name}
          selectedUserId={selectedUserId}
          onTimeCardAdded={handleMemberAdded}
        />
      );
    }

    switch (activeTab) {
      case 'Admin':
        return <Admin members={members.Admin} selectedUserId={selectedUserId} selectedDate={selectedDate} refreshKey={refreshKey} />;
      case 'Manager':
        return <Manager members={members.Manager} selectedUserId={selectedUserId} selectedDate={selectedDate} refreshKey={refreshKey} />;
      case 'Cleaner':
        return <Cleaner members={members.Cleaner} selectedUserId={selectedUserId} selectedDate={selectedDate} refreshKey={refreshKey} />;
      case 'Contractor':
        return <Contractor members={members.Contractor} selectedUserId={selectedUserId} selectedDate={selectedDate} refreshKey={refreshKey} />;
      default:
        return <Admin members={members.Admin} selectedUserId={selectedUserId} selectedDate={selectedDate} refreshKey={refreshKey} />;
    }
  };

  return (
    <div className="dashboard">
      {/* === Top Row === */}
      <div className="timecard-topbar">
     

        {/* Center: Title */}
        <h4 className="timecard-title">Time Card</h4>

        {/* Right: Add Button */}
        <div className="nav-right">
          <button
            className="timecard-add-btn"
            onClick={() => patch({ action: 'add' })}
          >
            <FiPlus size={18} /> Add Manually
          </button>
        </div>
      </div>
   {/* Left: Tabs */}
        <div className="nav-left">
          {['Admin', 'Manager', 'Cleaner', 'Contractor'].map((tab) => (
            <button
              key={tab}
              className={`nav-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => patch({ tab })}
              disabled={showAddCard}
            >
              {tab}
            </button>
          ))}
        </div>
      {/* === Second Row === */}
      <div className="timecard-navbar">
        {/* Left: Member Select */}
        <div className="nav-left">
          <select 
            className="member-select"
            value={selectedUserId || ''}
            onChange={(e) => patch({ member: e.target.value })}
          >
            {members[activeTab]?.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
        </div>

        {/* Middle: Date Controls */}
        <div className="nav-middle">
          <div className="date-controls">
            <button onClick={() => handleDateChange('prev')}>{'<'}</button>
            <span onClick={() => handleDateChange('today')} style={{ cursor: 'pointer' }}>
              {formatDateDisplay(selectedDate)}
            </span>
            <button onClick={() => handleDateChange('next')}>{'>'}</button>
          </div>
        </div>

        {/* Right: Empty (for balance) */}
        <div className="nav-right"></div>
      </div>

      {/* Rendered Component */}
      <div className="main-content">{renderComponent()}</div>
    </div>
  );
}
